import {getAppTracer} from '../../app.tracer';

const trace = getAppTracer('hash-url-redirect');

/**
 * Extract path and query params from hash portion of URL
 * Example: #/invite?roomId=123 → {path: '/invite', params: {roomId: '123'}}
 */
function parseHashUrl(hash: string): {path: string; params: URLSearchParams} {
    // Remove leading # if present
    const hashContent = hash.startsWith('#') ? hash.substring(1) : hash;

    // Split path and query string
    const [path, queryString] = hashContent.split('?');

    // Parse query parameters
    const params = new URLSearchParams(queryString || '');

    return {
        path: path || '/',
        params
    };
}

/**
 * Merge two sets of URL search params
 * Hash params take precedence over pre-hash params
 */
function mergeParams(preHashParams: URLSearchParams, hashParams: URLSearchParams): URLSearchParams {
    const merged = new URLSearchParams(preHashParams);

    // Add/override with hash params
    hashParams.forEach((value, key) => {
        merged.set(key, value);
    });

    return merged;
}

/**
 * Check if URL contains hash routing and redirect to non-hash version
 * Should be called BEFORE React Router initializes
 * @returns true if redirect was performed, false otherwise
 */
export function checkAndRedirectHashUrl(): boolean {
    const currentUrl = window.location.href;
    const hash = window.location.hash;

    // No hash or empty hash - no redirect needed
    if (!hash || hash === '#' || hash === '#/') {
        return false;
    }

    // Check if hash contains a route (starts with #/)
    if (!hash.startsWith('#/')) {
        return false;
    }

    trace.info('Hash URL detected, performing backward compatibility redirect', {
        originalUrl: currentUrl,
        hash
    });

    try {
        // Parse hash content
        const {path, params: hashParams} = parseHashUrl(hash);

        // Get pre-hash query params (rare but possible)
        const preHashParams = new URLSearchParams(window.location.search);

        // Merge params (hash params override pre-hash params)
        const mergedParams = mergeParams(preHashParams, hashParams);

        // Build new URL without hash
        const newUrl = new URL(window.location.origin + window.location.pathname);
        newUrl.pathname = path;
        newUrl.search = mergedParams.toString();

        trace.info('Redirecting to non-hash URL', {
            originalUrl: currentUrl,
            newUrl: newUrl.href,
            extractedPath: path,
            params: Object.fromEntries(mergedParams)
        });

        // Use replace to avoid adding to browser history
        window.location.replace(newUrl.href);

        return true;
    } catch (error) {
        trace.error('Failed to parse and redirect hash URL', {
            error,
            hash
        });
        return false;
    }
}
